import {
  useAuthApiGetObjectUrls,
  useAuthApiGetSingleFetch,
  type ObjectUrlRouteStatus,
} from '@restDataAccess';
import {
  type FilterType,
  GalleryGatewayEndpoints,
  type FishResponse,
  type WelfareType,
} from '@scaleaq/sitecontrol-lib';
import { DateTime } from 'luxon';

import { API_NAMES, CACHE_KEYS } from '@/constants';

import { toGatewayImageRoute, type FishCropImageRequest } from './orbitOneGateway.functions.ts';

/** site-control typically requests a small page of the highest-scoring fish. */
const DEFAULT_FISH_LIMIT = 9;

type UseGetFishObservationsParams = {
  /** OrbitOne device inventory id (required; maps to the gateway's `inventoryId`). */
  inventoryId: string | undefined;
  /** Single calendar day to query (defaults applied by the caller, e.g. yesterday). */
  date: DateTime;
  /** Welfare classes to filter on. Empty/omitted sends no `welfare` filter (all fish). */
  welfare?: WelfareType[];
  /** Lice or Welfare classes to filter on. Empty/omitted sends no `filter` (all fish); any selection is sent as-is. */
  filter?: FilterType[];
  /** Page size (default {@link DEFAULT_FISH_LIMIT}). */
  limit?: number;
  enabled?: boolean;
};

/**
 * Fetches fish observations for an OrbitOne device on a single calendar day from the gallery gateway
 * ({@link GalleryGatewayEndpoints.getFishCandidates}). `date` is sent as `YYYY-MM-DD` with the
 * date's IANA zone as `tz`, so the day boundaries match what the user sees. Selected lice
 * {@link FilterType} values are sent as a single comma-separated `filter` query param.
 */
export const useGetFishObservations = ({
  inventoryId,
  date,
  welfare,
  filter,
  limit = DEFAULT_FISH_LIMIT,
  enabled = true,
}: UseGetFishObservationsParams) => {
  const day = date.toFormat('yyyy-MM-dd');

  const filterSorted = [...(filter ?? [])].sort();
  const filterKey = filterSorted.length ? filterSorted.join(',') : 'all';

  const welfareSorted = [...(welfare ?? [])].sort();
  const welfareKey = welfareSorted.length ? welfareSorted.join(',') : 'all';

  const params = new URLSearchParams({
    inventoryId: inventoryId ?? '',
    date: day,
    limit: String(limit),
  });
  if (date.zoneName) params.set('tz', date.zoneName);

  if (filterSorted.length) params.set('filter', filterSorted.join(','));
  if (welfareSorted.length) params.set('welfare', welfareSorted.join(','));

  return useAuthApiGetSingleFetch<FishResponse>({
    baseApiRoute: API_NAMES.galleryGatewayApi,
    route: `${GalleryGatewayEndpoints.getFishCandidates}?${params.toString()}`,
    cacheKeys: [CACHE_KEYS.fishObservations, inventoryId, day, filterKey, welfareKey, limit],
    enabled: enabled && Boolean(inventoryId),
    refetchOnWindowFocus: false,
  });
};

export type FishCropImageStatus = 'idle' | 'loading' | 'error';

export interface FishCropImages {
  /** Map of the original crop path → a browser object URL ready for `<img src>`. */
  urls: Record<string, string>;
  /** Aggregate status across all crops (`error` if any failed, `loading` while any is pending). */
  status: FishCropImageStatus;
  /** Per-crop status, so a single failing/slow image does not put every thumbnail in `loading`. */
  statusByPath: Record<string, FishCropImageStatus>;
}

type UseFishCropImagesArgs = {
  paths: (string | undefined)[];
  request?: FishCropImageRequest;
};

/**
 * Resolves authenticated fish-crop images to object URLs usable by `<img>`/lightbox.
 *
 * Thin wrapper over {@link useAuthApiGetObjectUrls}: crop paths are normalized to gateway-relative
 * routes (keeping only `pathname`+`search`) so requests stay same-origin and go through the dev
 * proxy in local mode rather than re-triggering CORS on an absolute `localhost` URL. The shared hook
 * fetches each crop with `authorizationHeader`, caches the blob, and manages the object-URL
 * lifecycle.
 * Results are re-keyed back to the original crop paths for callers.
 *
 * NOTE: assumes crops are served by the same gateway. If production returns absolute CDN URLs that
 * should be loaded as-is, pass those through unchanged instead of {@link toGatewayImageRoute}.
 */
export const useFishCropImages = ({ paths, request }: UseFishCropImagesArgs): FishCropImages => {
  const pathRoutes = paths
    .filter((path): path is string => Boolean(path))
    .map(path => [path, toGatewayImageRoute({ path, request })] as const);

  const { urls, statusByRoute, isLoading, isError } = useAuthApiGetObjectUrls({
    baseApiRoute: API_NAMES.galleryGatewayApi,
    routes: pathRoutes.map(([, route]) => route),
    cacheKey: CACHE_KEYS.fishCropImage,
  });

  const urlsByPath: Record<string, string> = {};
  const statusByPath: Record<string, FishCropImageStatus> = {};
  for (const [path, route] of pathRoutes) {
    const url = urls[route];
    if (url) urlsByPath[path] = url;
    statusByPath[path] = toFishCropStatus(statusByRoute[route]);
  }

  return {
    urls: urlsByPath,
    status: isError ? 'error' : isLoading ? 'loading' : 'idle',
    statusByPath,
  };
};

/** Map the shared per-route status onto the gallery's `idle | loading | error` vocabulary. */
const toFishCropStatus = (status: ObjectUrlRouteStatus | undefined): FishCropImageStatus => {
  if (status === 'error') return 'error';
  if (status === 'success') return 'idle';
  return 'loading';
};
